import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { MailIcon, SmartphoneIcon } from "lucide-react";

import { languages } from "simplydo/core/i18n";
import {
  DEFAULT_ORG_TIMEZONE,
  getTimezoneOptions,
} from "simplydo/core/timezones";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H3, H5, Subheader } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import type {
  OnOrganisationEditedFn,
  OrganisationSettingsUpdate,
  SettingsOwnerOrganisation,
  SubscriptionMethod,
} from "../../types";
import AdminSidebar from "./AdminSidebar";
import IpRestriction from "./IpRestriction";
import PointOfContact from "./PointOfContact";

export const SegmentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  gap: 12px;
  margin-bottom: 12px;

  ${Card} {
    margin: 0;
    flex: 1 0 40%;
    width: 100%;
  }
`;

const possibleMethods: { key: "email" | "push"; icon: IconData }[] = [
  { key: "email", icon: MailIcon },
  { key: "push", icon: SmartphoneIcon },
];

type SubscriptionItem = (typeof constants.subscriptions)[number] & {
  unsupportedMethods?: string[];
};

type DefaultSubscriptionsMap = Record<string, SubscriptionMethod[]>;

type SubscriptionProps = {
  sub: SubscriptionItem;
  hasSub: (key: string, method: SubscriptionMethod) => boolean;
  toggleSub: (
    key: string,
    method: SubscriptionMethod,
    checked?: boolean,
  ) => void;
};

function Subscription({ sub, hasSub, toggleSub }: SubscriptionProps) {
  const { t } = useTranslation();

  return (
    <div
      key={sub.key}
      style={{
        marginTop: 5,
        marginBottom: 5,
        paddingTop: 3,
        paddingBottom: 3,
        borderBottom: "1px solid #e9ebee",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {t(sub.text)}
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        {possibleMethods.map((m) => (
          <Checkbox
            disabled={(sub.unsupportedMethods?.indexOf(m.key) ?? -1) > -1}
            data-tooltip={
              (sub.unsupportedMethods?.indexOf(m.key) ?? -1) > -1
                ? "This method is not available"
                : null
            }
            style={{ margin: 5 }}
            key={m.key}
            checked={hasSub(sub.key, m.key)}
            onClick={(_e, c) => toggleSub(sub.key, m.key, c.checked)}
          />
        ))}
      </div>
    </div>
  );
}

function NotificationSectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "bottom",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <H5 style={{ display: "block", marginBottom: 0 }}>{title}</H5>
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        {possibleMethods.map((m) => (
          <Icon key={m.key} icon={m.icon} style={{ margin: 5 }} />
        ))}
      </div>
    </div>
  );
}

const deletionTimeframe = [
  { key: "never", text: "Never" },
  { key: 183, text: "After 6 months of inactivity" },
  { key: 365, text: "After 12 months of inactivity" },
  { key: 730, text: "After 24 months of inactivity" },
] as const;

const ideas = constants.subscriptions.filter((s) => s.type === "ideas");
const challenges = constants.subscriptions.filter(
  (s) => s.type === "challenges",
);
const groups = constants.subscriptions.filter((s) => s.type === "groups");
const organisations = constants.subscriptions.filter(
  (s) => s.type === "organisations",
);
const general = constants.subscriptions.filter((s) => s.type === "general");

const SettingsGeneral = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onOrganisationEdited = useCallback<OnOrganisationEditedFn>(
    (field, value) => dispatch(actions.user.updateOrganisation(field, value)),
    [dispatch],
  );

  const ownerOrganisation = user?.ownerOrganisation as
    | SettingsOwnerOrganisation
    | undefined;
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [userIp, setUserIp] = useState("");
  const orgId = ownerOrganisation?._id;

  const { t } = useTranslation();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  useEffect(() => {
    if (showErrorMessage) {
      window.scrollTo(0, 0);
    }
  }, [showErrorMessage]);

  const getUserIpInfo = useCallback(() => {
    if (!orgId) return;
    api.organisations.getIpAddress(
      orgId,
      ({ ip }) => {
        setUserIp(ip ?? "");
      },
      () => {},
    );
  }, [orgId]);

  const saveName = useCallback(() => {
    if (!ownerOrganisation?._id) return;
    setShowErrorMessage(false);
    api.organisations.updateSettings(
      ownerOrganisation._id,
      { name: ownerOrganisation.name },
      () => toast.success(t("generic.typeUpdated", { type: "Settings" })),
      () => setShowErrorMessage(true),
    );
  }, [t, ownerOrganisation]);

  useEffect(() => {
    getUserIpInfo();
  }, [getUserIpInfo]);

  const updateUserAccountSelfDeletion = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { disableAccountSelfDeletion: !enabled },
        () => {
          onOrganisationEdited("disableAccountSelfDeletion", !enabled);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const changeDefaultLanguage = useCallback(
    (defaultLanguage: string) => {
      if (!ownerOrganisation?._id) return;
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultLanguage },
        () => {
          onOrganisationEdited("defaultLanguage", defaultLanguage);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const changeDefaultTimezone = useCallback(
    (defaultTimezone: string) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultTimezone },
        () => {
          onOrganisationEdited("defaultTimezone", defaultTimezone);
          toast.success(t("generic.typeUpdated", { type: "Settings" }));
        },
        () => setShowErrorMessage(true),
      );
    },
    [t, ownerOrganisation, onOrganisationEdited],
  );

  const hasSub = useCallback(
    (key: string, method: SubscriptionMethod) => {
      if (!ownerOrganisation) return false;
      const subs: DefaultSubscriptionsMap = {
        ...(ownerOrganisation.defaultSubscriptions as DefaultSubscriptionsMap),
      };
      const sub = subs[key];
      return Boolean(sub && sub.indexOf(method) > -1);
    },
    [ownerOrganisation],
  );

  const toggleSub = useCallback(
    (key: string, method: SubscriptionMethod, checked?: boolean) => {
      if (!ownerOrganisation?._id) return;
      const subs: DefaultSubscriptionsMap = {
        ...(ownerOrganisation.defaultSubscriptions as DefaultSubscriptionsMap),
      };
      let methods = [...(subs[key] || [])];
      if (!methods.length && checked) methods = [method];
      if (methods.length) {
        const index = methods.indexOf(method);
        if (checked && index === -1) methods.push(method);
        if (!checked && index > -1) methods.splice(index, 1);
      }
      subs[key] = methods;
      setShowErrorMessage(false);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { defaultSubscriptions: subs },
        () => {
          onOrganisationEdited("defaultSubscriptions", subs);
        },
        () => setShowErrorMessage(true),
      );
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  const toggleNotifications = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      if (enabled) {
        util
          .confirm(
            "Default notifications",
            "Are you sure you want to edit the default notifications of your organisation? If you do so, all new users will start with the email/push notification settings that you choose.",
          )
          .then(() => {
            api.organisations.updateSettings(
              ownerOrganisation._id,
              {
                defaultSubscriptions:
                  constants.defaultSubscriptions as OrganisationSettingsUpdate["defaultSubscriptions"],
              },
              () => {
                onOrganisationEdited(
                  "defaultSubscriptions",
                  constants.defaultSubscriptions,
                );
              },
              () => setShowErrorMessage(true),
            );
          })
          .catch(() => {});
      } else {
        api.organisations.updateSettings(
          ownerOrganisation._id,
          { defaultSubscriptions: null },
          () => {
            onOrganisationEdited("defaultSubscriptions", null);
          },
          () => setShowErrorMessage(true),
        );
      }
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  const changeAutoDeletionTime = useCallback(
    (value: string | number) => {
      if (!ownerOrganisation?._id) return;
      setShowErrorMessage(false);
      const deleteAfter = value === "never" ? null : (value as number);
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { autoAccountDeletionPolicy: { deleteAfter } },
        () => {
          onOrganisationEdited("autoAccountDeletionPolicy", { deleteAfter });
        },
        () => setShowErrorMessage(true),
      );
    },
    [ownerOrganisation, onOrganisationEdited],
  );

  if (!ownerOrganisation || !user) {
    return null;
  }

  return (
    <div>
      {showErrorMessage ? (
        <Message color="red">
          <MessageHeader>There was a problem</MessageHeader>
          <MessageContent>
            We were unable to save your settings. You may not have the correct
            permissions. Please contact an administrator for further advice.
          </MessageContent>
        </Message>
      ) : null}

      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.general.organisation.title")}
              <Subheader>
                {t("admin.settings.general.organisation.info")}
              </Subheader>
            </H3>
            <Input
              fluid
              value={ownerOrganisation.name}
              onChange={(e) => onOrganisationEdited("name", e.target.value)}
              action={
                <Button onClick={saveName} color="primary">
                  {t("generic.save")}
                </Button>
              }
            />
          </CardContent>
        </Card>
        <Card fluid>
          <CardContent>
            <H3>
              {t("admin.settings.general.language.title")}
              <Subheader>
                {t("admin.settings.general.language.info", { user })}
              </Subheader>
            </H3>
            <Dropdown
              variant="outline"
              fluid
              value={ownerOrganisation.defaultLanguage || "en"}
              options={languages.map((l) => ({
                key: l.key,
                value: l.key,
                text: l.name,
              }))}
              onChange={(_e, s) =>
                changeDefaultLanguage(
                  typeof s.value === "string" ? s.value : "en",
                )
              }
            />
            <H3>
              {t("admin.settings.general.timezone.title")}
              <Subheader>
                {t("admin.settings.general.timezone.info", { user })}
              </Subheader>
            </H3>
            <Dropdown
              variant="outline"
              search
              fluid
              value={ownerOrganisation.defaultTimezone || DEFAULT_ORG_TIMEZONE}
              options={timezoneOptions}
              onChange={(_e, s) => {
                if (typeof s.value === "string") {
                  changeDefaultTimezone(s.value);
                }
              }}
            />
            <Message>
              Your current timezone is{" "}
              {Intl.DateTimeFormat().resolvedOptions().timeZone}.
            </Message>
          </CardContent>
        </Card>
      </SegmentContainer>

      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              Automatically delete inactive users
              <Subheader>
                <p>
                  {util.appName()} can automatically delete user accounts in{" "}
                  {ownerOrganisation.name} according to a schedule that you
                  customise.
                </p>
                <p>
                  Choose an option below to schedule user accounts to be deleted
                  after the selected time has elapsed since their last-seen
                  date. Users will be sent warning emails a few weeks before
                  their accounts are deleted.
                </p>
              </Subheader>
            </H3>
            <Message color="orange" size="small">
              <MessageHeader>Automated deletion notice</MessageHeader>
              <MessageContent>
                All content such as {t("generic.ideas")}, messages, and comments
                belonging to affected user accounts will also be deleted.
              </MessageContent>
            </Message>
            <div style={{ display: "flex", alignItems: "center" }}>
              <p style={{ marginRight: 10 }}>
                <strong>Delete user accounts</strong>
              </p>
              <Dropdown
                variant="outline"
                value={
                  ownerOrganisation?.autoAccountDeletionPolicy?.deleteAfter ||
                  "never"
                }
                options={deletionTimeframe.map((l) => ({
                  key: l.key,
                  value: l.key,
                  text: l.text,
                }))}
                onChange={(_e, s) =>
                  changeAutoDeletionTime(
                    typeof s.value === "string" || typeof s.value === "number"
                      ? s.value
                      : "never",
                  )
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card fluid>
          <CardContent>
            <H3>
              Allow users to delete their own accounts
              <Subheader>
                By default in {util.appName()}, users can delete their own
                accounts. By disabling this, users will no longer be able to
                delete their own accounts, and only administrators will be able
                to delete users from {util.appName()}.
              </Subheader>
            </H3>
            <Checkbox
              toggle
              checked={!ownerOrganisation?.disableAccountSelfDeletion}
              onChange={(_e, c) => updateUserAccountSelfDeletion(c.checked)}
            >
              Allow users to delete their own accounts
            </Checkbox>
          </CardContent>
        </Card>
      </SegmentContainer>

      <SegmentContainer>
        <Card fluid>
          <CardContent>
            <H3>
              Default notification subscriptions
              <Subheader>
                {util.appName()} will automatically assign a default set of
                notification subscriptions to new users as they join{" "}
                {ownerOrganisation.name}. If you prefer, you can configure your
                own default notification subscriptions below by choosing when
                users should receive emails and push notifications.
              </Subheader>
            </H3>
            <Checkbox
              style={{ marginBottom: 10 }}
              toggle
              checked={!!ownerOrganisation.defaultSubscriptions}
              onChange={(_e, c) => toggleNotifications(c.checked)}
            >
              Customise event subscriptions
            </Checkbox>

            {ownerOrganisation.defaultSubscriptions && (
              <div>
                <p>
                  To reset to {util.appName()}
                  &apos;s default notification subscriptions set, please
                  deselect the toggle above.
                </p>
                <NotificationSectionHeader
                  title={t("users.preferences.yourAccount.title")}
                />
                {general.map((sub) => (
                  <Subscription
                    key={sub.key}
                    sub={sub}
                    hasSub={hasSub}
                    toggleSub={toggleSub}
                  />
                ))}

                <Divider hidden />
                <NotificationSectionHeader
                  title={t("common:capitalise", { key: "generic.groups" })}
                />
                {groups.map((sub) => (
                  <Subscription
                    key={sub.key}
                    sub={sub}
                    hasSub={hasSub}
                    toggleSub={toggleSub}
                  />
                ))}

                <Divider hidden />
                <NotificationSectionHeader
                  title={t("common:capitalise", { key: "generic.ideas" })}
                />
                {ideas.map((sub) => (
                  <Subscription
                    key={sub.key}
                    sub={sub}
                    hasSub={hasSub}
                    toggleSub={toggleSub}
                  />
                ))}

                <Divider hidden />
                <NotificationSectionHeader
                  title={t("users.preferences.yourChallenges.title")}
                />
                {challenges.map((sub) => (
                  <Subscription
                    key={sub.key}
                    sub={sub}
                    hasSub={hasSub}
                    toggleSub={toggleSub}
                  />
                ))}

                <Divider hidden />
                <NotificationSectionHeader title="Organisation" />
                {organisations.map((sub) => (
                  <Subscription
                    key={sub.key}
                    sub={sub}
                    hasSub={hasSub}
                    toggleSub={toggleSub}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <IpRestriction userIp={userIp} />
      </SegmentContainer>
      <SegmentContainer>
        <PointOfContact />
        <AdminSidebar />
      </SegmentContainer>
    </div>
  );
};

export default SettingsGeneral;
